import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CameraCapture from '../components/CameraCapture.jsx';
import { compressDataUrl } from '../utils/image.js';
import { api } from '../utils/api.js';
import { useAccessibility } from '../contexts/AccessibilityContext.jsx';
import { readLocal, readLocalJson, writeLocal, KEYS, TECH_NAME_EVENT } from '../utils/storage.js';
import { clearIdentity } from '../utils/identity.js';
import { friendlyError } from '../utils/errors.js';

// Same key as Result.jsx — Home reads (but never writes) the cached scan so
// it can offer a one-tap path back to the active result.
const LAST_SCAN_KEY = 'lastScan';

const PROGRESS_STAGES = [
  'Reading data tag…',
  'Identifying brand…',
  'Looking up service portal…',
];

const UNIT_PROGRESS = [
  'Analyzing the unit…',
  'Identifying brand & type…',
  'Searching for the model…',
];

export default function Home() {
  const [showCamera, setShowCamera] = useState(false);
  // 'tag' reads the data tag; 'unit' identifies the whole appliance ("No Data Tag").
  const [scanMode, setScanMode] = useState('tag');
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState(0);
  const [error, setError] = useState(null);
  // The compressed photo of a scan that failed, so Try again can resend it.
  const [failedShot, setFailedShot] = useState(null);
  const [techName, setTechName] = useState(readLocal(KEYS.techName, ''));
  // Signed in through Google (D-LAUNCH2): the name comes from the account and
  // isn't editable here. Re-read when the Layout's identity check lands.
  const [techEmail, setTechEmail] = useState(readLocal(KEYS.techEmail, ''));
  useEffect(() => {
    const sync = () => {
      // Only re-read the name when an email is present: this fires on every
      // keystroke of the typed-name field too (saveTechName dispatches it),
      // and a storage-quota failure on read must not overwrite what the tech
      // just typed with a stale stored name.
      const e = readLocal(KEYS.techEmail, '');
      setTechEmail(e);
      if (e) setTechName(readLocal(KEYS.techName, ''));
    };
    window.addEventListener(TECH_NAME_EVENT, sync);
    return () => window.removeEventListener(TECH_NAME_EVENT, sync);
  }, []);
  const [workOrder, setWorkOrder] = useState('');
  const navigate = useNavigate();
  const { dyslexicFont, setDyslexicFont } = useAccessibility();

  // If the tech has an active scan stashed, redirect them straight to /result.
  // This makes the scan "session" sticky: tapping the bottom-nav Scan Tag tab
  // while a scan is live no longer dumps them on Home (where the dominant
  // button is Step 1: Scan Data Tag, which a habit-tap would replace the
  // active scan with a new one). The session only ends when the tech
  // explicitly hits "+ New Scan" or "Done — Scan a New Tag" on Result.
  //
  // We still track activeScan in state for the rare race where the redirect
  // hasn't fired yet (first paint) — the resume card below acts as a
  // fallback affordance until navigate() takes effect.
  const [activeScan, setActiveScan] = useState(null);
  useEffect(() => {
    function readActive() {
      const parsed = readLocalJson(LAST_SCAN_KEY);
      // A scan is "active" if it identified anything. A No-Data-Tag unit scan
      // can legitimately have a null model_number but a real brand/type, and
      // must still be resumable — otherwise it's orphaned in localStorage.
      const r = parsed?.result;
      return r && (r.model_number || r.brand || r.appliance_type) ? parsed : null;
    }
    const initial = readActive();
    if (initial) {
      // replace:true so the back button doesn't trap the tech in a Home/Result
      // navigation loop.
      navigate('/result', { replace: true });
      return;
    }
    setActiveScan(initial);
    // Re-check on focus so a new scan that just happened in another tab/window
    // (rare on iPad but possible on desktop dev) refreshes the resume state.
    function onFocus() {
      const next = readActive();
      if (next) {
        navigate('/result', { replace: true });
      } else {
        setActiveScan(null);
      }
    }
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [navigate]);

  function saveTechName(name) {
    setTechName(name);
    writeLocal(KEYS.techName, name);
    // Defensive: this field only renders when there's no stored email, but
    // clear it anyway so a previous person's Access email can never ride
    // along with a freshly typed name (Task 5 review addendum). Only do this
    // when there's actually an email to clear — removeLocal on every
    // keystroke is wasted work once the field is already clean. Exactly one
    // TECH_NAME_EVENT fires either way: clearIdentity() dispatches it itself,
    // so the explicit dispatch below only runs on the other branch — that's
    // what lets the Layout name gate re-read in this same tab (if the tech
    // blanks the field, the gate should reappear before they can reach a page
    // whose actions would 401 without a name).
    if (techEmail) clearIdentity();
    else window.dispatchEvent(new Event(TECH_NAME_EVENT));
  }

  // Open the capture screen in a given mode. 'tag' reads the data tag; 'unit'
  // identifies the whole appliance when the tag can't be read.
  function startCapture(mode) {
    setScanMode(mode);
    setError(null);
    setFailedShot(null);
    setShowCamera(true);
  }

  async function handleCapture(dataUrl) {
    setShowCamera(false);
    let compressed;
    try {
      compressed = await compressDataUrl(dataUrl);
    } catch (e) {
      console.error(e);
      setError(friendlyError(e, 'read that photo'));
      return;
    }
    await analyze(compressed, scanMode);
  }

  // Send a compressed photo. On failure the photo is KEPT (failedShot), so
  // Try again resends it instead of sending the tech back to the tag on the
  // back panel; Retake is the separate way to shoot a new one.
  async function analyze(image, mode) {
    setBusy(true);
    setError(null);
    setFailedShot(null);
    setStage(0);
    const stageTimer = setInterval(() => {
      setStage((s) => Math.min(s + 1, PROGRESS_STAGES.length - 1));
    }, 1200);
    try {
      const result = await api.analyzeTag({ image, workOrder, mode });
      navigate('/result', { state: { result, capturedImage: image } });
    } catch (e) {
      console.error(e);
      setFailedShot({ image, mode });
      setError(friendlyError(e, mode === 'unit' ? 'identify that appliance' : 'read that data tag'));
    } finally {
      clearInterval(stageTimer);
      setBusy(false);
    }
  }

  function retake() {
    setError(null);
    setFailedShot(null);
    setShowCamera(true);
  }

  return (
    <>
      {showCamera && (
        <CameraCapture
          onCapture={handleCapture}
          onCancel={() => setShowCamera(false)}
          prompt={scanMode === 'unit'
            ? 'Frame the whole appliance — front-on, with any brand badges visible'
            : 'Center the appliance data tag in the frame'}
        />
      )}

      <div className="px-5 py-6 space-y-6 max-w-3xl mx-auto">
        {/* Tech name + per-tech accessibility prefs */}
        <div className="card border-mra-red/40 space-y-4">
          {techEmail ? (
            <div>
              <span className="text-ink-200 text-sm font-medium mb-1 block">Signed in as</span>
              <p className="text-lg text-ink-50 font-semibold">{techName}</p>
              <p className="text-ink-400 text-sm">{techEmail}</p>
            </div>
          ) : (
            <label className="block">
              <span className="text-ink-200 text-sm font-medium mb-2 block">
                Your name (so we know who's logging jobs)
              </span>
              <input
                type="text"
                placeholder="e.g. Mike R."
                value={techName}
                onChange={(e) => saveTechName(e.target.value)}
                autoCorrect="off"
                spellCheck={false}
                className="w-full px-4 py-3 bg-ink-800 border border-ink-700 rounded-lg text-white placeholder-ink-500 focus:outline-none focus:border-mra-red"
              />
            </label>
          )}

          {/* OpenDyslexic toggle — per-tech preference, persists to localStorage */}
          <DyslexicFontToggle
            checked={dyslexicFont}
            onChange={setDyslexicFont}
          />
        </div>

        {/* Resume current scan — shown when localStorage has an unfinished scan
            from a prior session/tab. Tapping it goes to /result, which will
            pick up the stashed scan and render exactly where the tech left off.
            The "+ New Scan" button on Result clears the cache. */}
        {activeScan?.result && (
          <button
            type="button"
            onClick={() => navigate('/result')}
            className="block w-full text-left bg-mra-red/10 border border-mra-red/40 rounded-2xl p-4 active:scale-[0.99] transition"
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl shrink-0">🔧</span>
              <div className="min-w-0 flex-1">
                <div className="text-mra-red text-xs font-mono uppercase tracking-widest mb-1">
                  Continue current scan
                </div>
                <div className="text-white font-mono text-base font-bold break-all">
                  {activeScan.result.model_number ||
                    activeScan.result.appliance_type ||
                    activeScan.result.brand ||
                    'Unidentified unit'}
                </div>
                {(activeScan.result.brand_matched || activeScan.result.brand) && (
                  <div className="text-ink-300 text-xs break-words">
                    {activeScan.result.brand_matched || activeScan.result.brand}
                    {activeScan.result.appliance_type ? ` · ${activeScan.result.appliance_type}` : ''}
                  </div>
                )}
              </div>
              <span className="text-mra-red text-lg shrink-0">→</span>
            </div>
          </button>
        )}

        {/* Hero scan card */}
        <div className="card overflow-hidden relative">
          <div className="absolute top-0 right-0 w-48 h-48 bg-mra-red/10 blur-3xl rounded-full -mr-12 -mt-12 pointer-events-none" />
          <div className="relative">
            <div className="text-mra-red text-xs font-mono uppercase tracking-widest mb-2">
              Step 1
            </div>
            <h1 className="h-display text-3xl text-white mb-2">Scan Data Tag</h1>
            <p className="text-ink-200 mb-6">
              Photograph the metal/sticker label on the appliance. We'll identify the model and brand and pull up its manual for troubleshooting.
            </p>

            <input
              type="text"
              inputMode="numeric"
              placeholder="Work Order # (optional)"
              value={workOrder}
              onChange={(e) => setWorkOrder(e.target.value)}
              className="w-full px-4 py-3 mb-4 bg-ink-800 border border-ink-700 rounded-lg text-white placeholder-ink-500 focus:outline-none focus:border-mra-red"
            />

            <button
              onClick={() => startCapture('tag')}
              disabled={busy || !techName}
              className="btn-primary w-full text-lg"
            >
              {busy ? (scanMode === 'unit' ? UNIT_PROGRESS : PROGRESS_STAGES)[stage] : '📷  Scan Tag'}
            </button>

            {/* No Data Tag → identify the whole appliance (best-guess model). */}
            <button
              onClick={() => startCapture('unit')}
              disabled={busy || !techName}
              className="btn-secondary w-full mt-3"
            >
              🔎  No Data Tag — Identify Unit
            </button>
            <p className="text-ink-400 text-xs mt-2 text-center">
              Can't find the tag? Photograph the whole appliance and we'll take a best guess at the model.
            </p>

            {!techName && (
              <p className="text-ink-300 text-xs mt-3 text-center">Enter your name above to start</p>
            )}
          </div>
        </div>

        {error && (
          <div className="card border-fail/60 bg-fail/10">
            <p className="text-white font-medium mb-3">⚠ {error}</p>
            {failedShot ? (
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => { setScanMode(failedShot.mode); analyze(failedShot.image, failedShot.mode); }}
                  disabled={busy}
                  className="btn-primary flex-1"
                >
                  ↻ Try again with this photo
                </button>
                <button onClick={retake} disabled={busy} className="btn-secondary flex-1">
                  📷 Retake photo
                </button>
              </div>
            ) : (
              <button onClick={retake} className="btn-ghost text-mra-red">
                Try Again
              </button>
            )}
          </div>
        )}

        {/* How it works */}
        <details className="card cursor-pointer">
          <summary className="text-ink-100 font-semibold list-none flex items-center justify-between min-h-[44px]">
            How it works
            <span className="text-ink-400 text-sm">tap</span>
          </summary>
          <ol className="mt-4 space-y-3 text-ink-200">
            <li className="flex gap-3">
              <span className="font-mono text-mra-red shrink-0">1.</span>
              <span>Snap a photo of the data tag — usually behind/under/inside the unit</span>
            </li>
            <li className="flex gap-3">
              <span className="font-mono text-mra-red shrink-0">2.</span>
              <span>AI extracts the model #, brand, serial #, and appliance type</span>
            </li>
            <li className="flex gap-3">
              <span className="font-mono text-mra-red shrink-0">3.</span>
              <span>Open the manufacturer portal, upload tech sheets, or get AI troubleshooting</span>
            </li>
          </ol>
        </details>
      </div>
    </>
  );
}

/**
 * Per-tech opt-in for OpenDyslexic. Lives next to the tech-name input on Home
 * so each tech sees it on first scan and can flip it without going to Admin.
 * Preference persists to localStorage via AccessibilityProvider.
 */
function DyslexicFontToggle({ checked, onChange }) {
  return (
    <label
      className={`flex items-center gap-3 p-3 rounded-lg border transition cursor-pointer ${
        checked
          ? 'bg-accent-900/40 border-accent-700'
          : 'bg-ink-800 border-ink-700 hover:border-ink-600'
      }`}
    >
      <span
        className={`relative inline-flex w-11 h-6 rounded-full transition shrink-0 ${
          checked ? 'bg-accent-500' : 'bg-ink-700'
        }`}
      >
        <span
          className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
            checked ? 'translate-x-5' : 'translate-x-0.5'
          }`}
        />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-ink-100 text-sm font-medium">Use OpenDyslexic font</span>
        <span className="block text-ink-300 text-xs">
          Dyslexia-friendly typeface across the app. Saved to this iPad.
        </span>
      </span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
        aria-label="Use OpenDyslexic font"
      />
    </label>
  );
}
